/**
 * The `choice` directive renderer — the wizard's describe-or-upload step. A
 * single-select `Radio` group; the chosen option's `value` is echoed back to the
 * waiting run on confirm.
 */
import type { UiDirective } from '@4f/shared';
import { Button, Form, Radio, useT } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { GenerativeUiHandlers } from '../GenerativeUi.tsx';

type ChoiceDirective = Extract<UiDirective, { kind: 'choice' }>;

export interface ChoiceRendererProps {
  readonly directive: ChoiceDirective;
  readonly handlers: GenerativeUiHandlers;
}

export function ChoiceRenderer(props: ChoiceRendererProps): JSX.Element {
  const { t } = useT();
  const [selected, setSelected] = createSignal(props.directive.options[0]?.value ?? '');

  const submit = (): void => props.handlers.onSubmit(selected());

  return (
    <Form onSubmit={submit}>
      <Show when={props.directive.title}>
        {(title) => <h3 class="f4-genui__title">{title()}</h3>}
      </Show>
      <Show when={props.directive.prompt}>
        {(prompt) => <p class="f4-genui__prompt">{prompt()}</p>}
      </Show>
      <Radio
        name={`choice-${props.directive.id}`}
        legend={props.directive.title ?? props.directive.prompt ?? t('surface.confirm')}
        options={props.directive.options.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        value={selected()}
        onChange={setSelected}
      />
      <Button type="submit" variant="primary" disabled={props.handlers.disabled}>
        {t('surface.confirm')}
      </Button>
    </Form>
  );
}
